import { useEffect, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import CreateMailVaultExport from './forms/create-mail-vault-export';
import CreateChatVaultExport from './forms/create-chat-vault-export';
import CreateCalendarVaultExport from './forms/create-calendar-vault-export';
import CreateGroupsVaultExport from './forms/create-groups-vault-export';
import CreateDriveVaultExport from './forms/create-drive-vault-export';
import CreateGeminiVaultExport from './forms/create-gemini-vault-export';
import {
  corpusOptions,
  exportFormatOptionsByCorpus,
} from './vault-export-constants';

import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export interface CreateVaultExportFormValues {
  name: string;
  corpus: string;
  dataScope: string;
  searchMethod: string;
  searchMethodTarget?: string;
  startTime?: string;
  endTime?: string;
  terms?: string;
  timeZone: string;
  exportFormat?: string;
  vaultExcludeDrafts?: boolean;
  includeChatSpaces?: boolean;
  showIndirectAccess?: boolean;
  matterRegion?: string;
  methodTargetVersionDate?: string;
  peopleQuery?: string[];
  locationQuery?: string[];
  minusWords?: string[];
  responseStatuses?: string[];
  sharedDrivesOption?: string;
}

interface CreateVaultExportFormProps {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  register: UseFormRegister<CreateVaultExportFormValues>;
  errors: FieldErrors<CreateVaultExportFormValues>;
  control: Control<CreateVaultExportFormValues>;
  toolsRoles: string[];
  setValue: UseFormSetValue<CreateVaultExportFormValues>;
  selectedSearchMethod: string;
  selectedCorpus: string;
  permissionByCorpus: Record<string, string | undefined>;
  matterName?: string;
}

export default function CreateVaultExportForm({
  onSubmit,
  register,
  errors,
  control,
  toolsRoles,
  setValue,
  selectedSearchMethod,
  selectedCorpus,
  permissionByCorpus,
  matterName,
}: CreateVaultExportFormProps) {
  const exportFormatOptions = exportFormatOptionsByCorpus[selectedCorpus] || [];

  const permittedCorpusOptions = useMemo(() => {
    if (isAbleToSeeUIElement(toolsRoles, VaultRoles.CreateVaultExports.value)) {
      return corpusOptions;
    }

    return corpusOptions.filter((option) => {
      const permission = permissionByCorpus[option.value];
      return !!permission && isAbleToSeeUIElement(toolsRoles, permission);
    });
  }, [toolsRoles, permissionByCorpus]);

  useEffect(() => {
    if (
      permittedCorpusOptions.length > 0 &&
      !permittedCorpusOptions.some((option) => option.value === selectedCorpus)
    ) {
      setValue('corpus', permittedCorpusOptions[0].value);
    }

    // Reset export format when corpus changes
    if (selectedCorpus === 'CALENDAR') {
      setValue('exportFormat', 'PST');
    } else if (selectedCorpus === 'GEMINI') {
      setValue('exportFormat', 'XML');
    } else if (selectedCorpus === 'DRIVE') {
      setValue('exportFormat', undefined);
    } else {
      // Default for MAIL
      setValue('exportFormat', 'MBOX');
    }

    // Reset fields on corpus change to prevent cross-contamination
    setValue('searchMethodTarget', '');
    setValue('terms', '');
    setValue('startTime', '');
    setValue('endTime', '');
    setValue('vaultExcludeDrafts', false);
    setValue('includeChatSpaces', false);
    setValue('responseStatuses', []);
    setValue('dataScope', 'ALL_DATA');
    setValue('searchMethod', 'ACCOUNT');
  }, [permittedCorpusOptions, selectedCorpus, setValue]);

  return (
    <form id="create-vault-export-form" onSubmit={onSubmit}>
      <Stack gap={1} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Configure and create a new export for{' '}
          <Typography
            component="span"
            variant="body2"
            sx={{ fontWeight: 'bold' }}
          >
            {matterName}
          </Typography>
          . Download your export promptly, it will be deleted <b>15 days</b>{' '}
          after export date.
        </Typography>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <FormControl sx={{ width: '50%', mt: 2 }}>
              <TextField
                {...field}
                label="Export Name"
                size="small"
                required
                error={!!errors.name}
                helperText={errors?.name?.message}
              />
            </FormControl>
          )}
        />

        <Controller
          name="corpus"
          control={control}
          render={({ field }) => (
            <FormControl sx={{ width: '50%', mt: 1 }} size="small">
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Service
              </Typography>
              <TextField
                {...field}
                select
                required
                error={!!errors.corpus}
                helperText={errors?.corpus?.message}
              >
                {permittedCorpusOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          )}
        />

        {selectedCorpus === 'MAIL' && (
          <CreateMailVaultExport
            control={control}
            errors={errors}
            register={register}
            selectedSearchMethod={selectedSearchMethod}
            exportFormatOptions={exportFormatOptions}
          />
        )}

        {selectedCorpus === 'HANGOUTS_CHAT' && (
          <CreateChatVaultExport
            control={control}
            errors={errors}
            register={register}
            selectedSearchMethod={selectedSearchMethod}
            exportFormatOptions={exportFormatOptions}
          />
        )}

        {selectedCorpus === 'CALENDAR' && (
          <CreateCalendarVaultExport
            control={control}
            errors={errors}
            register={register}
            selectedSearchMethod={selectedSearchMethod}
            exportFormatOptions={exportFormatOptions}
          />
        )}

        {selectedCorpus === 'GROUPS' && (
          <CreateGroupsVaultExport
            control={control}
            errors={errors}
            register={register}
            selectedSearchMethod={selectedSearchMethod}
            exportFormatOptions={exportFormatOptions}
          />
        )}

        {selectedCorpus === 'GEMINI' && (
          <CreateGeminiVaultExport
            control={control}
            errors={errors}
            register={register}
            selectedSearchMethod={selectedSearchMethod}
            exportFormatOptions={exportFormatOptions}
          />
        )}

        {selectedCorpus === 'DRIVE' && (
          <CreateDriveVaultExport
            control={control}
            errors={errors}
            register={register}
            selectedSearchMethod={selectedSearchMethod}
            setValue={setValue}
          />
        )}
      </Stack>
    </form>
  );
}
